import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IMessageModel, MessageSeverity } from "~app/common/components/message-panel/message-panel.models";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { IRaskSearchHeaderOption } from "~app/common/components/search/search.component";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { RaskCommonModule } from "../../../../../common/rask-common.module";
import { LeverantorsKlassning } from "../../models/leverantorsKlassning";
import { LeverantorsstatusSearchFilter } from "../../models/leverantorsstatusSearchFilter";
import { VirkesleverantorService } from "../../services/virkesleverantor.service";
import { MoveLeverantorResponseDTO, SearchVirkesleverantorForMoveRequestDTO, SearchVirkesleverantorForMoveViewModel } from "./models/searchvirkesleverantorer.models";

@Component({
    selector: "rask-move-leverantorer-dialog",
    templateUrl: "./move-leverantorer-dialog.component.html",
    styleUrl: "./move-leverantorer-dialog.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RaskCommonModule,
        TranslatePipe
    ]
})
export class MoveLeverantorerDialogComponent implements OnInit {
    modal = inject(NgbActiveModal);

    protected ansvarigVirkeskopareFrom: ITypeAheadResult;
    protected ansvarigVirkeskopareFromHeaderOptions: IRaskSearchHeaderOption<ITypeAheadResult>[] = [
        {
            data: {
                namn: "Icke tilldelade",
                id: -1,
                aktiv: false
            }
        }
    ];
    protected feedbackItems = new BehaviorSubject<IMessageModel[]>([]);
    protected form: UntypedFormGroup;
    protected isAllChecked = false;
    protected leverantorer: SearchVirkesleverantorForMoveViewModel[] = [];
    protected leverantorklassningOptions: SelectionItem[] = [
        {
            text: "A - Ambassadör",
            value: LeverantorsKlassning.A
        },
        {
            text: "B - Intressanta",
            value: LeverantorsKlassning.B
        },
        {
            text: "C - Övriga",
            value: LeverantorsKlassning.C
        },
        {
            text: "D - Vill ej bli kontaktade",
            value: LeverantorsKlassning.D
        },
        {
            text: "Ej klassad",
            value: null
        }
    ];
    protected leverantorsstatusOptions: SelectionItem[] = [
        {
            value: LeverantorsstatusSearchFilter.Anbudsleverantor,
            text: "Anbudsleverantör"
        },
        {
            value: LeverantorsstatusSearchFilter.Virkesleverantor,
            text: "Leverantör"
        }
    ];

    private readonly busyService = inject(BusyService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly virkesleverantorService = inject(VirkesleverantorService);

    ngOnInit(): void {
        this.clearAll();
    }

    protected ansvarigVirkeskopareComparer(item: ITypeAheadResult, selected: ITypeAheadResult) {
        return item?.id === selected?.id;
    }

    protected changedAnsvarigVirkeskopareFrom(value: ITypeAheadResult) {
        this.ansvarigVirkeskopareFrom = value;
    }

    protected checkAll(state: boolean): void {
        this.isAllChecked = state;
        this.leverantorer.forEach(a => {
            a.selected = state;
        });
    }

    protected clearAll() {
        this.clearResult();
        this.ansvarigVirkeskopareFrom = null;
        this.form = new UntypedFormGroup({
            ansvarigVirkeskopareTo: new UntypedFormControl(null),
            klassning: new UntypedFormControl(""),
            leverantorsstatus: new UntypedFormControl(null)
        });
    }

    protected clearFeedback() {
        this.feedbackItems.next([]);
    }

    protected clearResult() {
        this.leverantorer = [];
    }

    protected close() {
        this.modal.close();
    }

    protected formatter = (item: SelectionItem) => item?.value;

    protected getSelectedCount(): number {
        return this.leverantorer.filter(x => !!x.selected).length;
    }

    protected hasMoveData(): boolean {
        const toVirkeskopare = this.form.get("ansvarigVirkeskopareTo")?.value;
        return (this.getSelectedCount() > 0 && toVirkeskopare !== null && toVirkeskopare !== undefined);
    }

    protected hasSearchFilter(): boolean {
        const value = this.ansvarigVirkeskopareFrom?.id;
        return (value !== null && value !== undefined);
    }

    protected moveSelectedLeverantorer(): void {
        this.clearFeedback();
        const fromAnvandareId = this.ansvarigVirkeskopareFrom.id;
        const toAnvandareId = this.form.get("ansvarigVirkeskopareTo").value;
        const moveLeverantorIds = this.leverantorer.filter(l => l.selected === true).map(l => l.id);

        this.raskModalService.confirm(new ModalSettings("Flytta leverantörer", `Är du säker på att du vill flytta ${moveLeverantorIds?.length} leverantörer?`))
            .subscribe(dialogResult => {
                if (dialogResult === ConfirmDialogResponse.save) {
                    this.busyService.addWork(this.virkesleverantorService.moveLeverantorer(fromAnvandareId, toAnvandareId, moveLeverantorIds))
                        .subscribe({
                            next: (response) => {
                                if (response.movedLeverantorIds.length > 0) {
                                    this.leverantorer = this.leverantorer.filter(lev => !response.movedLeverantorIds.includes(lev.id));
                                }
                                if (response.failedMoves.length > 0) {
                                    this.showFailedLeverantorerFeedback(response);
                                }
                            },
                            error: (err) => {
                                this.showFeedback(MessageSeverity.Critical, "Fel inträffade. Försök igen eller kontakta support.");
                                this.clearResult();
                            }
                        });
                }
            });
    }

    protected searchForAnsvarigVirkeskopareTypeAhead = (input: string) => this.virkesleverantorService.getVirkesKopareTypeAhead(input);

    protected searchLeverantorer() {
        this.clearResult();
        this.clearFeedback();
        const request = new SearchVirkesleverantorForMoveRequestDTO();
        request.ansvarigVirkeskopareId = this.ansvarigVirkeskopareFrom.id;
        request.klassning = this.form.get("klassning").value;
        request.leverantorsstatus = this.form.get("leverantorsstatus").value;
        this.busyService.addWork(this.virkesleverantorService.searchForVirkeskopsleverantorerForMove(request))
            .subscribe(result => {
                this.leverantorer = [];
                if (result.length > 0) {
                    this.leverantorer = result.map(x => new SearchVirkesleverantorForMoveViewModel(x));
                }
                this.checkAll(false);
            });
    }

    protected selectedChanged($event) {
        this.isAllChecked = !this.leverantorer.some(a => a.selected === false);
    }

    protected textFormatter = (item: SelectionItem) => item?.text;

    private showFailedLeverantorerFeedback(response: MoveLeverantorResponseDTO) {
        const messages = response.failedMoves.map(x => ({
            message: `Leverantör med leverantörnummer '${x.limeId}' kunde inte uppdateras`,
            severity: MessageSeverity.Error,
            anchor: ""
        }));
        this.feedbackItems.next(messages);
    }

    private showFeedback(severity: MessageSeverity, messageText: string) {
        this.feedbackItems.next([
            {
                message: messageText,
                severity: severity,
                anchor: ""
            }
        ]);
    }
}
